import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock } from 'lucide-react';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, authError } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // SAFETY TIMEOUT (Jose 2026-09-24): if a read wedges (e.g. a stale bundle before the
      // long-polling fix), login() could hang forever → "Signing in…" spins with no escape. Race it
      // against 20s so it fails with a retryable error instead of trapping the user.
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Sign-in is taking too long — check your connection and reload the page, then try again.')), 20000));
      await Promise.race([login(email, password), timeout]);
      // HARD navigate (full reload), NOT react-router navigate (Jose 2026-09-24): on the native
      // WKWebView the SPA navigate races the async onAuthStateChanged update, so ProtectedRoute sees
      // "no user yet" and bounces back to /login → stuck on the signing page (session still saved,
      // which is why reopening the app shows you logged in). A full load reads the persisted session
      // and lands logged-in. Keep the spinner until the reload takes over.
      window.location.assign('/calendar');
    } catch (err: any) {
      setError(err.message || 'Failed to login');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #2A211A 0%, #C0824A 100%)' }}
    >
      {/* Engraved SLIK mark on the brand gradient — the same transparent look as the splash. */}
      <img
        src={`${process.env.PUBLIC_URL || ''}/assets/branding/shop-logo.png`}
        onError={(e) => { const t = e.currentTarget; if (!t.src.endsWith('logo512.png')) t.src = `${process.env.PUBLIC_URL || ''}/logo512.png`; }}
        alt="SLIK"
        className="slik-brandmark w-28 h-28 mb-5"
      />
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
          <p className="text-gray-500 mt-1 text-sm">Sign in to SLIK</p>
        </div>

        {(error || authError) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error || authError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your password"
                required
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center">
              <input type="checkbox" className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <span className="ml-2 text-sm text-gray-600">Remember me</span>
            </label>
            <button type="button" className="text-sm text-indigo-600 hover:text-indigo-500 bg-transparent border-none cursor-pointer">
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full min-h-[44px] touch-manipulation bg-[#C0824A] text-white py-2.5 px-4 rounded-lg font-medium hover:bg-[#a96f3c] focus:outline-none focus:ring-2 focus:ring-[#C0824A] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Setting up a barber's device? Don't sign up — ask the owner for an activation code, then{' '}
            <Link to="/activate" className="font-medium text-[#a96f3c] hover:text-[#8a5a30] underline">enter it here</Link>.
          </p>
        </div>

      </div>
      <div className="mt-6 text-[11px] tracking-[0.12em] uppercase font-semibold text-white/60">
        powered by <span className="text-white/90 font-bold">NEXSY</span>
      </div>
    </div>
  );
};

export default LoginPage;